import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AppShell,
  Box,
  Burger,
  List,
  Notification,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import {
  IconUser,
  IconBriefcase,
  IconFolderBolt,
  IconCreditCard,
  IconGift,
  IconFolder,
  IconBell,
  IconFileText,
  IconLink,
  IconFlask,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import Header from "../../components/Tax Pro Only/Header/Header";
import Sidebar from "../../components/Sidebar/Sidebar";
import PersonalInfoSetting from "../PersonalInfoSetting/PersonalInfoSetting";
import BusinessInfoSetting from "../BusinessInfoSetting/BusinessInfoSetting";
import MainHeader from "../../components/Tax Client Only/MainHeader/MainHeader";
import ChangePassword from "../../components/ChangePassword/ChangePassword";
import ReminderSettings from "../../components/Tax Pro Only/ReminderSettings/ReminderSettings";
import type { PersonalInfoSettings } from "@/types";
import {
  changePassword,
  getNotificationSetings as getNotificationSettings,
  updateNotificationSetings,
  updateTaxProInfo,
} from "../../utils/api";
import BusinessLogo from "../../components/Tax Pro Only/BusinessLogo/BusinessLogo";
import Billing from "@/pages/Billing/Billing";
import "./Settings.scss";
import { BUSINESS_STAFF } from "@/constants.ts";
import Can, { AbilityContext } from "../../Can";
import { useAbility } from "@casl/react";
import type { Subjects } from "@utils/abilities.ts";
import MobileButtons from "../../components/Tax Client Only/MobileButtons/MobileButtons";
import OrganizerSettings from "../../components/Tax Pro Only/OrganizerSettings/OrganizerSettings";
import NavigationWarningModal from "./NavigationWarningModal/NavigationWarningModal";
import EngagementLetterSettings from "../../components/Tax Pro Only/EngagementLetterSettings/EngagementLetterSettings";
import SettingsHeader from "./SettingsHeader/SettingsHeader";
import ScrollableTabs from "./ScrollableTabs/ScrollableTabs";
import { useSearchParams, useNavigate } from "react-router-dom";
import { TeamManager } from "@/components/Team/TeamManager/TeamManager";
import OneClickSettings from "./components/OneClickSettings";
import { useRole, withRoleBasedLoading } from "@/components/CASLProvider";
import { RETURN_LABEL } from "../Credits/constants";
import { ReferralSettings } from "./components/ReferralSettings";
import { ReferralInstructions } from "./components/ReferralSettings/ReferralInstructions";
import { SmartIntakeInviteLink } from "@components/SmartIntakeInviteLink";
import { useAuthStore } from "@utils/stores/authStore.ts";
import { isIntakeFrontend } from "@utils/frontendMigration.ts";
import { AppRoutes } from "@/constants.ts";
import AlphaPrograms from "./AlphaPrograms/AlphaPrograms";

/**
 * Renders the Settings component, which displays the settings page based on the user's role.
 *
 * @return {JSX.Element} The settings page component
 */
const Settings: React.FC = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const toggle: () => void = () => (opened ? close() : open());
  const [notification, setNotification] = useState<{
    title: string;
    message: string;
    color: string;
  } | null>(null);
  const { role, isClient } = useRole();
  const navigate = useNavigate();
  const { user, shouldHideBillingSettings } = useAuthStore();
  const hideBilling = shouldHideBillingSettings();

  const ability = useAbility(AbilityContext);

  const canViewAnyNotifications =
    ability.can("view", "EmailNotificationPreferences") ||
    ability.can("view", "ClientEmailNotificationPreferences") ||
    ability.can("view", "ClientReminderPreferences");

  const canViewOrganizerSettings = ability.can("view", "Organizer");
  const isIntakeFrontendBusiness = useAuthStore((state) =>
    isIntakeFrontend(state.user)
  );

  const storedPersonalInfo = localStorage.getItem("personalInfo");
  const initialPersonalInfo = storedPersonalInfo
    ? JSON.parse(storedPersonalInfo)
    : {
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
      };
  const [personalInfo, setPersonalInfo] =
    useState<PersonalInfoSettings>(initialPersonalInfo);

  const storedBusinessInfo = localStorage.getItem("businessInfo");
  const initialBusinessInfo = storedBusinessInfo
    ? JSON.parse(storedBusinessInfo)
    : {
        businessName: "",
        businessPhone: "",
        businessEmail: "",
        businessWebsite: "",
        logoKey: "",
        consultLink: "",
        paymentLink: "",
        primaryColor: "",
        secondaryColor: "",
        loginMessage: "",
      };
  const [businessInfo, setBusinessInfo] = useState(initialBusinessInfo);

  const [password, setPassword] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [settings, setSettings] = useState<Record<
    string,
    string | number | boolean
  > | null>(null);

  const [isActiveTab, setIsActiveTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "Account";
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Check if there are unsaved changes, and show warning modal if there are
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const tabLabels = BUSINESS_STAFF.includes(role as string)
    ? [
        "Account",
        "Business",
        "Team",
        ...(hideBilling ? [] : ["Billing"]),
        "Referral",
        isIntakeFrontendBusiness ? "Smart Intake" : "",
        canViewOrganizerSettings ? "Organizer" : "",
        canViewAnyNotifications ? "Notifications" : "",
        "Engagement Letter",
        "Alpha Programs",
      ].filter((label) => {
        if (!label) return false;
        if (label === "Smart Intake") return true;
        return ability.can("view", label as Subjects);
      })
    : ["Account"];

  // Icon mapping for each tab
  const tabIcons = useMemo(
    () => ({
      Account: <IconUser size={18} />,
      Business: <IconBriefcase size={18} />,
      Team: <IconFolderBolt size={18} />,
      Billing: <IconCreditCard size={18} />,
      Referral: <IconGift size={18} />,
      Organizer: <IconFolder size={18} />,
      Notifications: <IconBell size={18} />,
      "Engagement Letter": <IconFileText size={18} />,
      "Smart Intake": <IconLink size={18} />,
      "Alpha Programs": <IconFlask size={18} />,
    }),
    [RETURN_LABEL]
  );

  useEffect(() => {
    const fetchSettings: () => Promise<void> = async () => {
      try {
        const response = await getNotificationSettings();
        setSettings(response?.data?.content);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchSettings();
  }, []);

  // Redirect to lite home if user should use lite home (similar to Stripe redirect logic)
  useEffect(() => {
    console.log("[Settings] Redirect check triggered", {
      user: user?.email,
      role,
      defaultTab,
      hasTabParam: searchParams.has("tab"),
      userObject: user,
    });

    // Only redirect for business staff users (not clients)
    if (!user) {
      console.log("[Settings] No user - skipping redirect");
      return;
    }

    if (!BUSINESS_STAFF.includes(role as string)) {
      console.log("[Settings] Not business staff - skipping redirect", {
        role,
      });
      return;
    }

    // Check if user should use lite home (defaults to true for new businesses)
    const useLiteHome = user.useLiteHome ?? true;
    console.log("[Settings] useLiteHome check", {
      useLiteHome,
      userUseLiteHome: user.useLiteHome,
    });

    if (useLiteHome) {
      // Map settings tab to lite home settings page
      const settingsTabToLiteSettingMap: Record<string, string> = {
        Account: "account",
        Business: "business",
        Team: "team",
        Billing: "billing",
      };

      // If no tab param is present (e.g., coming from Stripe), default to billing
      // Otherwise use the specified tab
      const targetTab = searchParams.has("tab")
        ? defaultTab
        : hideBilling
          ? "Account"
          : "Billing";
      const liteSettingParam = settingsTabToLiteSettingMap[targetTab];

      console.log("[Settings] Determining redirect target", {
        hasTabParam: searchParams.has("tab"),
        defaultTab,
        targetTab,
        liteSettingParam,
      });

      if (liteSettingParam) {
        const redirectUrl = `${AppRoutes.homeLite}?setting=${liteSettingParam}`;
        console.log("[Settings] Redirecting to lite home with setting", {
          redirectUrl,
        });
        navigate(redirectUrl, {
          replace: true,
        });
      } else {
        console.log(
          "[Settings] Tab not mapped - redirecting to lite home without setting",
          { targetTab }
        );
        navigate(AppRoutes.homeLite, { replace: true });
      }
    } else {
      console.log("[Settings] useLiteHome is false - staying on settings page");
    }
  }, [user, role, defaultTab, searchParams, navigate, hideBilling]);

  // Sync active tab with URL changes
  useEffect(() => {
    setActiveTab(defaultTab);
    const tabIndex = tabLabels.indexOf(defaultTab);
    setIsActiveTab(tabIndex >= 0 ? tabIndex : 0);
  }, [searchParams, defaultTab, tabLabels]);

  useEffect(() => {}, [hasUnsavedChanges]);

  const teamGuideContent = (
    <Accordion
      variant="default"
      chevronPosition="left"
      styles={{
        item: {
          border: "none",
        },
      }}
    >
      <Accordion.Item value="guide">
        <Accordion.Control>
          <Text size="sm" fw={500}>
            Understanding Team Management & Return Allocation
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="lg">
            {/* Team Roles Section */}
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Team Roles & Permissions
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Owner:
                    </Text>{" "}
                    Full control over the account. Can add team members and team
                    leads, access all clients, manage all business settings, and
                    allocate returns to team members.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Team Lead:
                    </Text>{" "}
                    Can view all clients (even unassigned), assign clients to
                    team members, and invite new team members. Managed by return
                    allocation policy set by the owner.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Team Member:
                    </Text>{" "}
                    Can only view clients assigned to them. Managed by return
                    allocation policy set by the owner.
                  </Text>
                </List.Item>
              </List>
            </Stack>

            {/* Return Allocation Section */}
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Return Allocation Policies
              </Text>
              <Text size="sm" c="dimmed">
                Returns determine how many tax returns a team member can
                process. There are two access types:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Business Use:
                    </Text>{" "}
                    Team members can use their allocated returns for any client
                    in the business, providing maximum flexibility for teams
                    with shared workloads.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Limited Use:
                    </Text>{" "}
                    Team members can only use their allocated returns for
                    clients specifically assigned to them, ideal for managing
                    individual workloads and tracking utilization.
                  </Text>
                </List.Item>
              </List>
            </Stack>

            {/* Metered Billing Section */}
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                How Returns Work with Metered Billing
              </Text>
              <Text size="sm" c="dimmed">
                With metered billing, you pay based on usage:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>
                  <Text size="sm">
                    Returns are deducted for each client and tax year
                    combination activated. For example, activating both 2023 and
                    2024 tax years for one client uses 2 returns.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    Each tax return processed consumes one return credit from
                    the team member's allocated balance.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    Owners can set individual return allocations for each team
                    member to control costs and manage capacity.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    Team members can request additional returns when they run
                    low, which owners can approve or deny.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    The business maintains a central return balance that owners
                    use to allocate to team members as needed.
                  </Text>
                </List.Item>
              </List>
            </Stack>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );

  return (
    <AppShell
      className="client-page settings-page"
      header={{ height: 87 }}
      navbar={{
        width: "15.375rem",
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header className="app-header">
        {isClient ? (
          <MainHeader opened={opened} toggle={toggle} />
        ) : (
          <>
            <Burger
              className="burger"
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
            />
            <Header />
          </>
        )}
      </AppShell.Header>

      <AppShell.Navbar className="nav">
        <Sidebar />
      </AppShell.Navbar>
      <AppShell.Main className={`main `}>
        <Box px={{ base: 0, sm: "xl" }}>
          {notification && (
            <Notification
              title={notification.title}
              color={notification.color}
              onClose={() => setNotification(null)}
              withCloseButton
            >
              {notification.message}
            </Notification>
          )}
          <div className="container">
            <Stack className="stack" gap="lg">
              <Tabs
                activateTabWithKeyboard
                value={activeTab}
                onChange={(value) => {
                  const newTab = value || "Account";
                  setActiveTab(newTab);
                  setSearchParams({ tab: newTab });
                }}
              >
                <ScrollableTabs
                  tabLabels={tabLabels}
                  tabIcons={tabIcons}
                  activeTabIndex={isActiveTab}
                  onTabClick={(index) => {
                    setIsActiveTab(index);
                    const newTab = tabLabels[index];
                    setActiveTab(newTab);
                    setSearchParams({ tab: newTab });
                  }}
                />
                <Tabs.Panel value="Account" p={4}>
                  <>
                    <SettingsHeader
                      title="Account"
                      description="Manage your account information"
                    />
                    <PersonalInfoSetting
                      personalInfo={personalInfo}
                      setPersonalInfo={setPersonalInfo}
                    />
                    <ChangePassword
                      password={password}
                      setPassword={setPassword}
                      updatePassword={changePassword}
                    />
                  </>
                </Tabs.Panel>

                {BUSINESS_STAFF.includes(role as string) && (
                  <>
                    <Tabs.Panel value="Business">
                      <SettingsHeader
                        title="Business"
                        description="View and update your business information"
                      />
                      <Stack gap={48}>
                        <BusinessInfoSetting
                          businessInfo={businessInfo}
                          setBusinessInfo={setBusinessInfo}
                          updateBusinessInfo={updateTaxProInfo}
                        >
                          <BusinessLogo />
                        </BusinessInfoSetting>
                        <OneClickSettings />
                      </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="Notifications">
                      <SettingsHeader
                        title="Notifications"
                        description="Manage updates about your account activity"
                      />
                      <ReminderSettings
                        settings={settings}
                        setSettings={setSettings}
                        updateNotificationPreferences={
                          updateNotificationSetings
                        }
                        ability={ability}
                      />
                    </Tabs.Panel>
                    <Can I="view" a="Billing">
                      {!hideBilling && (
                        <Tabs.Panel value="Billing" pt={4}>
                          <SettingsHeader
                            title="Billing & Plan"
                            description="View and update your billing and plan information"
                          />
                          <Billing loading={false} />
                        </Tabs.Panel>
                      )}
                    </Can>
                    <Can I="view" a="Returns">
                      <Tabs.Panel value="Team" pt={4}>
                        <SettingsHeader
                          title="Team"
                          description={teamGuideContent}
                        />
                        <TeamManager />
                      </Tabs.Panel>
                    </Can>
                    <Can I="view" a="Organizer">
                      <Tabs.Panel value="Organizer">
                        <SettingsHeader
                          title="Organizer"
                          description="Manage how your clients view the tax organizer"
                        />
                        <OrganizerSettings
                          setHasUnsavedChanges={setHasUnsavedChanges}
                        />
                      </Tabs.Panel>
                      <Tabs.Panel value="Engagement Letter">
                        <SettingsHeader
                          title="Engagement Letter"
                          description="Manage the engagement letter your clients will receive before they start the tax organizer."
                        />
                        <EngagementLetterSettings />
                      </Tabs.Panel>
                    </Can>
                    <Tabs.Panel value="Referral" pt="md">
                      <Stack gap="md">
                        <SettingsHeader
                          title="Refer Friends, Earn Rewards"
                          description="Earn rewards for every friend who purchases SmartWiz"
                          infoContent={<ReferralInstructions />}
                        />
                        <ReferralSettings />
                      </Stack>
                    </Tabs.Panel>
                    {isIntakeFrontendBusiness && (
                      <Tabs.Panel value="Smart Intake" pt="md">
                        <Stack gap="md">
                          <SettingsHeader
                            title="Smart Intake"
                            description="Share your invite link with clients so they can create an account and upload their tax documents."
                          />
                          <SmartIntakeInviteLink />
                        </Stack>
                      </Tabs.Panel>
                    )}
                    <Tabs.Panel value="Alpha Programs" pt="md">
                      <Stack gap="md">
                        <SettingsHeader
                          title="Alpha Programs"
                          description="Get early access to features we're building. Join an alpha to try new capabilities before they're available to everyone."
                        />
                        <AlphaPrograms />
                      </Stack>
                    </Tabs.Panel>
                  </>
                )}
              </Tabs>
            </Stack>
          </div>
          {isClient && <MobileButtons />}
        </Box>
        {hasUnsavedChanges && (
          <NavigationWarningModal hasUnsavedChanges={hasUnsavedChanges} />
        )}
      </AppShell.Main>
    </AppShell>
  );
};
export default withRoleBasedLoading(Settings);
